/*
* Broadcast Theme
*
* Use this file to add custom Javascript to Broadcast.  Keeping your custom
* Javascript in this fill will make it easier to update Broadcast. In order
* to use this file you will need to open layout/theme.liquid and uncomment
* the custom.js script import line near the bottom of the file.
*/


(function() {
  // Add custom code below this line

  /* ==========================================================================
     Predictive search speed-ups (ideas ported from Shopify's Horizon theme)
     - Cancels the previous request when a new search starts, and never shows
       results for a query the shopper has already typed past.
     - Searches 200ms after typing stops (was 300ms).
     - Keeps product cards that are still in the results instead of redrawing
       them, so their images don't reload or flash.
     - Shows results instantly (no staggered fade-in) and lazy-loads images.
     - Clears old results as soon as a different search is typed.
     - Enter goes straight to the product when there is exactly one result.
     ========================================================================== */
  const PredictiveSearch = customElements.get('predictive-search');
  if (!PredictiveSearch || PredictiveSearch.prototype.__searchSpeedPatched) return;
  PredictiveSearch.prototype.__searchSpeedPatched = true;

  const SECTION_SELECTOR = '#shopify-section-predictive-search';
  const CARD_SELECTOR = '.predictive-search__grid-item';
  const AOS_ATTRIBUTES = ['data-aos', 'data-aos-delay', 'data-aos-duration', 'data-aos-anchor'];
  const LOADER_MARKUP =
    '<div class="predictive-search__loading-state"><div class="predictive-search__loader loader"><div class="loader-indeterminate"></div></div></div>';

  const cardKey = (card) => {
    const link = card.querySelector('a[href]');
    if (!link) return null;
    try {
      return new URL(link.getAttribute('href'), window.location.origin).pathname;
    } catch (error) {
      return null;
    }
  };

  // 1. Skip duplicate work: the theme's own 300ms listener still fires, but if
  //    our faster 200ms listener already handled this exact query, do nothing.
  const originalOnChange = PredictiveSearch.prototype.onChange;
  PredictiveSearch.prototype.onChange = function () {
    const query = this.getQuery();
    if (query && query === this.searchTerm) return;
    return originalOnChange.apply(this, arguments);
  };

  // 2. Cancel stale requests and ignore late responses.
  PredictiveSearch.prototype.getSearchResults = function (searchTerm) {
    const cacheKey = searchTerm.toLowerCase().replace(/\s+/g, '-');
    this.setLiveRegionLoadingState();

    if (this.cachedResults[cacheKey]) {
      this.renderSearchResults(this.cachedResults[cacheKey]);
      return;
    }

    this.abortController.abort();
    this.abortController = new AbortController();

    const url = `${theme.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`;

    fetch(url, { signal: this.abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((text) => {
        const section = new DOMParser().parseFromString(text, 'text/html').querySelector(SECTION_SELECTOR);
        if (!section) return;
        const markup = section.innerHTML;
        this.allPredictiveSearchInstances.forEach((instance) => {
          instance.cachedResults[cacheKey] = markup;
        });
        // Only show these results if the shopper hasn't typed something newer.
        if (searchTerm === this.searchTerm) this.renderSearchResults(markup);
      })
      .catch((error) => {
        if (error && error.name === 'AbortError') return;
        this.close();
      });
  };

  // 3. Render without animations, lazy-load images, and reuse cards already on screen.
  PredictiveSearch.prototype.renderSearchResults = function (markup) {
    const container = this.predictiveSearchResults;
    const template = document.createElement('template');
    template.innerHTML = markup;
    const fragment = template.content;

    fragment.querySelectorAll('[data-aos]').forEach((element) => {
      AOS_ATTRIBUTES.forEach((attribute) => element.removeAttribute(attribute));
    });

    fragment.querySelectorAll('img').forEach((image) => {
      image.setAttribute('loading', 'lazy');
      image.removeAttribute('fetchpriority');
    });

    const existingCards = new Map();
    container.querySelectorAll(CARD_SELECTOR).forEach((card) => {
      const key = cardKey(card);
      if (key) existingCards.set(key, card);
    });

    if (existingCards.size) {
      fragment.querySelectorAll(CARD_SELECTOR).forEach((card) => {
        const key = cardKey(card);
        const existing = key && existingCards.get(key);
        if (!existing) return;

        // Keep the fresh link so Shopify's search analytics stay accurate.
        const freshLinks = card.querySelectorAll('a[href]');
        const keptLinks = existing.querySelectorAll('a[href]');
        keptLinks.forEach((link, index) => {
          if (freshLinks[index]) link.setAttribute('href', freshLinks[index].getAttribute('href'));
        });

        existing.setAttribute('aria-selected', 'false');
        existingCards.delete(key);
        card.replaceWith(existing);
      });
    }

    container.replaceChildren(fragment);
    this.setAttribute('results', true);
    this.setLiveRegionResults();
    this.open();
  };

  const debounce = (fn, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn(...args), wait);
    };
  };

  document.querySelectorAll('predictive-search').forEach((search) => {
    const { input } = search;
    if (!input) return;

    // 0. A new search starts empty. On the search results page the theme fills
    //    the header search box with the previous search, so typing a new word
    //    was added on the end (e.g. "boots" + "sandal" = "bootssandal"). The
    //    results page keeps the previous search in its own search box.
    if (input.value && search.closest('[data-popdown]')) {
      input.value = '';
      if (typeof search.toggleResetButton === 'function') search.toggleResetButton();
    }

    //    As a safety net, if the box does contain text when the shopper opens
    //    search, select it so typing replaces it instead of adding to it.
    input.addEventListener('focus', () => {
      if (input.value) setTimeout(() => input.select(), 0);
    });

    // 4. Clear old results straight away when the shopper starts a different
    //    search (e.g. types over "boots" with "sandal"), instead of leaving the
    //    old products on screen until the new ones arrive. Results are kept
    //    while the shopper is just adding or removing letters on the same word.
    input.addEventListener('input', () => {
      const query = search.getQuery().toLowerCase();
      const shown = (search.searchTerm || '').toLowerCase();
      const sameSearch = query && shown && (query.startsWith(shown) || shown.startsWith(query));
      if (sameSearch) return;

      search.abortController.abort();
      search.abortController = new AbortController();
      search.searchTerm = '';
      const results = search.predictiveSearchResults;
      if (!results) return;
      results.innerHTML = query ? LOADER_MARKUP : '';
      if (query) search.setAttribute('loading', true);
    });

    // 5. Faster search trigger (200ms instead of 300ms).
    input.addEventListener('input', debounce(() => search.onChange(), 200));

    // 6. Enter with exactly one product result goes straight to that product.
    if (input.form) {
      input.form.addEventListener(
        'submit',
        (event) => {
          const query = search.getQuery();
          if (!query || query !== search.searchTerm || search.hasAttribute('loading')) return;
          if (search.querySelector('[aria-selected="true"] a, button[aria-selected="true"]')) return;

          const results = search.predictiveSearchResults;
          if (!results) return;
          const productLinks = results.querySelectorAll(`${CARD_SELECTOR} a[href]`);
          const otherResults = results.querySelectorAll(
            '[id^="predictive-search-option-collection"], [id^="predictive-search-option-page"], [id^="predictive-search-option-article"]'
          );

          if (productLinks.length === 1 && otherResults.length === 0) {
            event.preventDefault();
            event.stopImmediatePropagation();
            window.location.href = productLinks[0].href;
          }
        },
        true
      );
    }
  });

  // ^^ Keep your scripts inside this IIFE function call to 
  // avoid leaking your variables into the global scope.
})();
